import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useListEventsQuery } from '../../redux/api/eventApi';
import { useEffect, useState } from 'react';
import { selectEvent } from '../../redux/authSlice';

function EventSelector() {
  const { data: events } = useListEventsQuery();
  const authState = useSelector((state) => state.auth);
  const [selectedEvent, setSelectedEvent] = useState(
    authState?.currentEvent?.id || ''
  );
  const dispatch = useDispatch();

  // Auto-select first event if no current event is set
  useEffect(() => {
    if (!authState?.currentEvent && events?.length > 0) {
      setSelectedEvent(events[0].id);
      dispatch(selectEvent(events[0]));
    }
  }, [authState?.currentEvent, events, dispatch]);

  // Re-sync the stored event with the server's copy whenever the list loads.
  // currentEvent is persisted to localStorage, so without this a field that
  // changed server-side (above all `enabled`, which drives the read-only
  // banner) would stay stale for everyone who already had the event selected —
  // they would keep seeing it as open until they manually re-picked it.
  useEffect(() => {
    const current = authState?.currentEvent;
    if (!current || !events?.length) return;

    const fresh = events.find((ev) => ev.id === current.id);
    if (!fresh) {
      // The event vanished from the list entirely (deleted). Fall back rather
      // than leaving a dangling id every page would query against.
      setSelectedEvent(events[0].id);
      dispatch(selectEvent(events[0]));
      return;
    }
    // Compare serialized: the fetched object is a fresh reference every time,
    // so an identity check would dispatch on every render.
    if (JSON.stringify(fresh) !== JSON.stringify(current)) {
      dispatch(selectEvent(fresh));
    }
  }, [events, authState?.currentEvent, dispatch]);

  // Update local state when authState changes. Self-terminating: the setState
  // only fires while the two differ, so re-running on `selectedEvent` settles
  // after one extra pass rather than looping.
  useEffect(() => {
    if (authState?.currentEvent?.id !== selectedEvent) {
      setSelectedEvent(authState?.currentEvent?.id || '');
    }
  }, [authState?.currentEvent, selectedEvent]);

  const handleEventChange = (e) => {
    const newEventId = e.target.value;
    setSelectedEvent(newEventId);

    // Find the selected event object and dispatch it
    const selEvent = events?.find((ev) => ev.id === newEventId);
    if (selEvent) {
      dispatch(selectEvent(selEvent));
      const label =
        selEvent.enabled === false
          ? `${selEvent.name} (abgeschlossen — nur Lesezugriff)`
          : selEvent.name;
      toast.info(`Event gewechselt: ${label}`);
    }
  };

  return events && events.length > 0 ? (
    <select
      value={selectedEvent}
      onChange={handleEventChange}
      className='ms-2 form-select form-select-sm'
    >
      {events.map((ev) => (
        <option key={ev.id} value={ev.id}>
          {ev.enabled === false ? `${ev.name} (abgeschlossen)` : ev.name}
        </option>
      ))}
    </select>
  ) : null;
}

export default EventSelector;
